import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { Stack } from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import {
  setUsersData,
  setUsersLoading,
  setUsersLeadToQualifiedData,
  setUsersLeadToQualifiedLoading,
  setUsersQualifiedByZipData,
  setUsersQualifiedByZipLoading,
  setUsersLeadToPaidData,
  setUsersLeadToPaidLoading,
  setUsersLeadToMatchedData,
  setUsersLeadToMatchedLoading,
  setUsersStep1AllData,
  setUsersStep1AllLoading,
  setUsersStep1LeadData,
  setUsersStep1LeadLoading,
  setUsersStep1QualifiedData,
  setUsersStep1QualifiedLoading,
  setUsersStep2AllData,
  setUsersStep2AllLoading,
  setUsersStep2LeadData,
  setUsersStep2LeadLoading,
  setUsersStep2QualifiedData,
  setUsersStep2QualifiedLoading,
  setUsersStep3AllData,
  setUsersStep3AllLoading,
  setUsersStep3LeadData,
  setUsersStep3LeadLoading,
  setUsersStep3QualifiedData,
  setUsersStep3QualifiedLoading,
  setUsersStep4AllData,
  setUsersStep4AllLoading,
  setUsersStep4LeadData,
  setUsersStep4LeadLoading,
  setUsersStep4QualifiedData,
  setUsersStep4QualifiedLoading,
  setUsersStep5LeadData,
  setUsersStep5LeadLoading,
  setUsersStep5QualifiedData,
  setUsersStep5QualifiedLoading,
  setUsersStep6AllData,
  setUsersStep6AllLoading,
  setUsersStep6LeadData,
  setUsersStep6LeadLoading,
  setUsersStep6QualifiedData,
  setUsersStep6QualifiedLoading,
  setUsersStep7AllData,
  setUsersStep7AllLoading,
  setUsersStep7LeadData,
  setUsersStep7LeadLoading,
  setUsersStep7QualifiedData,
  setUsersStep7QualifiedLoading,
} from "../../../../../../../../features/optifundDashboardSlice";
import { OptifundDashboardQueryIds } from "../../../../../../../../pages/AssetManagement/Overview/OptifundDashboard/logic/OptifundDashboardQueryIds";
import { useOptifundTabData } from "../../../../hooks/useOptifundTabData";
import { resolveUsersDateRange } from "../../../../Users/usersDateDefaults";
import {
  DonutSegment,
  FunnelStep,
  mapBorrowAmountStepSection,
  mapBusinessBankingStepSection,
  mapBusinessIndustryStepSection,
  mapLeadToMatchedSegments,
  mapLeadToPaidSegments,
  mapLeadToQualifiedSegments,
  mapLoanPurposeStepSection,
  mapMainFunnelApiRows,
  mapMonthlyRevenueStepSection,
  mapPersonalCreditStepSection,
  mapQualifiedByZipSegments,
  mapTimeInBusinessStepSection,
  StepBarSection,
  StepDonutSection,
} from "../../../../Users/usersApiMapping";
import UsersFunnelDonuts from "../../../../Users/UsersFunnelDonuts";
import UsersDateFilter from "./UsersMobileContent/UsersDateFilter";
import UsersKpiCards from "./UsersMobileContent/UsersKpiCards";
import UsersFunnel from "./UsersMobileContent/UsersFunnel";
import UsersStepSections from "./UsersMobileContent/UsersStepSections";
import UsersSkel from "./UsersMobileContent/helpers/UsersSkel";

const EMPTY_STEPS: FunnelStep[] = [];
const EMPTY_SEGMENTS: DonutSegment[] = [];

interface UsersProps {
  userToken: any;
  websiteSharedUid: string;
}

// Mirrors the desktop Users.tsx's data-fetching wiring exactly (same 21
// queries, same Redux actions, same date range resolution) and, like
// desktop, composes progressively rather than behind one all-or-nothing
// gate: the funnel/KPIs/overview donuts render as soon as their own 3
// queries resolve, and each of the 7 step sections renders independently as
// its own 2-3 queries resolve (via UsersStepSections' per-section
// skeletons). Only the layout/markup differs from desktop (checklist
// Section 10.3/10.4).
export default function Users({ userToken, websiteSharedUid }: UsersProps) {
  const dispatch = useDispatch();
  // Each step's own *Loading flags (not just Array.isArray(...Data)) drive
  // its own readiness below - a refetch (date range change, or the parent's
  // refresh remount) flips these back to true immediately even though the
  // PREVIOUS successful data is still sitting in Redux, so the section
  // correctly shows its skeleton again instead of silently freezing on
  // stale content with no visible sign a reload is happening.
  const {
    usersData,
    usersLoading,
    usersLeadToQualifiedData,
    usersLeadToQualifiedLoading,
    usersQualifiedByZipData,
    usersQualifiedByZipLoading,
    usersLeadToPaidData,
    usersLeadToPaidLoading,
    usersLeadToMatchedData,
    usersLeadToMatchedLoading,
    usersStep1AllData,
    usersStep1AllLoading,
    usersStep1LeadData,
    usersStep1LeadLoading,
    usersStep1QualifiedData,
    usersStep1QualifiedLoading,
    usersStep2AllData,
    usersStep2AllLoading,
    usersStep2LeadData,
    usersStep2LeadLoading,
    usersStep2QualifiedData,
    usersStep2QualifiedLoading,
    usersStep3AllData,
    usersStep3AllLoading,
    usersStep3LeadData,
    usersStep3LeadLoading,
    usersStep3QualifiedData,
    usersStep3QualifiedLoading,
    usersStep4AllData,
    usersStep4AllLoading,
    usersStep4LeadData,
    usersStep4LeadLoading,
    usersStep4QualifiedData,
    usersStep4QualifiedLoading,
    usersStep5LeadData,
    usersStep5LeadLoading,
    usersStep5QualifiedData,
    usersStep5QualifiedLoading,
    usersStep6AllData,
    usersStep6AllLoading,
    usersStep6LeadData,
    usersStep6LeadLoading,
    usersStep6QualifiedData,
    usersStep6QualifiedLoading,
    usersStep7AllData,
    usersStep7AllLoading,
    usersStep7LeadData,
    usersStep7LeadLoading,
    usersStep7QualifiedData,
    usersStep7QualifiedLoading,
  } = useSelector((state: any) => state.optifundDashboard);

  const [searchParams, setSearchParams] = useSearchParams();
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>(() => {
    const start = searchParams.get("users_start");
    const end = searchParams.get("users_end");
    return start && end && dayjs(start).isValid() && dayjs(end).isValid()
      ? [dayjs(start), dayjs(end)]
      : [null, null];
  });

  useEffect(() => {
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        const [start, end] = dateRange;
        if (start && end) {
          next.set("users_start", start.format("YYYY-MM-DD"));
          next.set("users_end", end.format("YYYY-MM-DD"));
        } else {
          next.delete("users_start");
          next.delete("users_end");
        }
        return next;
      },
      { replace: true }
    );
  }, [dateRange, setSearchParams]);

  const [startDate, endDate] = resolveUsersDateRange(dateRange);
  const startDateParam = startDate.format("YYYY-MM-DD");
  const endDateParam = endDate.format("YYYY-MM-DD");

  useOptifundTabData(
    userToken,
    websiteSharedUid,
    OptifundDashboardQueryIds.usersMainFunnelId,
    (data) => dispatch(setUsersData(data)),
    (loading) => dispatch(setUsersLoading(loading)),
    startDateParam,
    endDateParam
  );

  useOptifundTabData(
    userToken,
    websiteSharedUid,
    OptifundDashboardQueryIds.usersLeadToQualifiedId,
    (data) => dispatch(setUsersLeadToQualifiedData(data)),
    (loading) => dispatch(setUsersLeadToQualifiedLoading(loading)),
    startDateParam,
    endDateParam
  );

  useOptifundTabData(
    userToken,
    websiteSharedUid,
    OptifundDashboardQueryIds.usersQualifiedByZipId,
    (data) => dispatch(setUsersQualifiedByZipData(data)),
    (loading) => dispatch(setUsersQualifiedByZipLoading(loading)),
    startDateParam,
    endDateParam
  );

  useOptifundTabData(
    userToken,
    websiteSharedUid,
    OptifundDashboardQueryIds.usersLeadToMatchedId,
    (data) => dispatch(setUsersLeadToMatchedData(data)),
    (loading) => dispatch(setUsersLeadToMatchedLoading(loading)),
    startDateParam,
    endDateParam
  );

  useOptifundTabData(
    userToken,
    websiteSharedUid,
    OptifundDashboardQueryIds.usersLeadToPaidId,
    (data) => dispatch(setUsersLeadToPaidData(data)),
    (loading) => dispatch(setUsersLeadToPaidLoading(loading)),
    startDateParam,
    endDateParam
  );

  useOptifundTabData(
    userToken,
    websiteSharedUid,
    OptifundDashboardQueryIds.usersStep1AllId,
    (data) => dispatch(setUsersStep1AllData(data)),
    (loading) => dispatch(setUsersStep1AllLoading(loading)),
    startDateParam,
    endDateParam
  );

  useOptifundTabData(
    userToken,
    websiteSharedUid,
    OptifundDashboardQueryIds.usersStep1LeadId,
    (data) => dispatch(setUsersStep1LeadData(data)),
    (loading) => dispatch(setUsersStep1LeadLoading(loading)),
    startDateParam,
    endDateParam
  );

  useOptifundTabData(
    userToken,
    websiteSharedUid,
    OptifundDashboardQueryIds.usersStep1QualifiedId,
    (data) => dispatch(setUsersStep1QualifiedData(data)),
    (loading) => dispatch(setUsersStep1QualifiedLoading(loading)),
    startDateParam,
    endDateParam
  );

  useOptifundTabData(
    userToken,
    websiteSharedUid,
    OptifundDashboardQueryIds.usersStep2AllId,
    (data) => dispatch(setUsersStep2AllData(data)),
    (loading) => dispatch(setUsersStep2AllLoading(loading)),
    startDateParam,
    endDateParam
  );

  useOptifundTabData(
    userToken,
    websiteSharedUid,
    OptifundDashboardQueryIds.usersStep2LeadId,
    (data) => dispatch(setUsersStep2LeadData(data)),
    (loading) => dispatch(setUsersStep2LeadLoading(loading)),
    startDateParam,
    endDateParam
  );

  useOptifundTabData(
    userToken,
    websiteSharedUid,
    OptifundDashboardQueryIds.usersStep2QualifiedId,
    (data) => dispatch(setUsersStep2QualifiedData(data)),
    (loading) => dispatch(setUsersStep2QualifiedLoading(loading)),
    startDateParam,
    endDateParam
  );

  useOptifundTabData(
    userToken,
    websiteSharedUid,
    OptifundDashboardQueryIds.usersStep3AllId,
    (data) => dispatch(setUsersStep3AllData(data)),
    (loading) => dispatch(setUsersStep3AllLoading(loading)),
    startDateParam,
    endDateParam
  );

  useOptifundTabData(
    userToken,
    websiteSharedUid,
    OptifundDashboardQueryIds.usersStep3LeadId,
    (data) => dispatch(setUsersStep3LeadData(data)),
    (loading) => dispatch(setUsersStep3LeadLoading(loading)),
    startDateParam,
    endDateParam
  );

  useOptifundTabData(
    userToken,
    websiteSharedUid,
    OptifundDashboardQueryIds.usersStep3QualifiedId,
    (data) => dispatch(setUsersStep3QualifiedData(data)),
    (loading) => dispatch(setUsersStep3QualifiedLoading(loading)),
    startDateParam,
    endDateParam
  );

  useOptifundTabData(
    userToken,
    websiteSharedUid,
    OptifundDashboardQueryIds.usersStep4AllId,
    (data) => dispatch(setUsersStep4AllData(data)),
    (loading) => dispatch(setUsersStep4AllLoading(loading)),
    startDateParam,
    endDateParam
  );

  useOptifundTabData(
    userToken,
    websiteSharedUid,
    OptifundDashboardQueryIds.usersStep4LeadId,
    (data) => dispatch(setUsersStep4LeadData(data)),
    (loading) => dispatch(setUsersStep4LeadLoading(loading)),
    startDateParam,
    endDateParam
  );

  useOptifundTabData(
    userToken,
    websiteSharedUid,
    OptifundDashboardQueryIds.usersStep4QualifiedId,
    (data) => dispatch(setUsersStep4QualifiedData(data)),
    (loading) => dispatch(setUsersStep4QualifiedLoading(loading)),
    startDateParam,
    endDateParam
  );

  useOptifundTabData(
    userToken,
    websiteSharedUid,
    OptifundDashboardQueryIds.usersStep5LeadId,
    (data) => dispatch(setUsersStep5LeadData(data)),
    (loading) => dispatch(setUsersStep5LeadLoading(loading)),
    startDateParam,
    endDateParam
  );

  useOptifundTabData(
    userToken,
    websiteSharedUid,
    OptifundDashboardQueryIds.usersStep5QualifiedId,
    (data) => dispatch(setUsersStep5QualifiedData(data)),
    (loading) => dispatch(setUsersStep5QualifiedLoading(loading)),
    startDateParam,
    endDateParam
  );

  useOptifundTabData(
    userToken,
    websiteSharedUid,
    OptifundDashboardQueryIds.usersStep6AllId,
    (data) => dispatch(setUsersStep6AllData(data)),
    (loading) => dispatch(setUsersStep6AllLoading(loading)),
    startDateParam,
    endDateParam
  );

  useOptifundTabData(
    userToken,
    websiteSharedUid,
    OptifundDashboardQueryIds.usersStep6LeadId,
    (data) => dispatch(setUsersStep6LeadData(data)),
    (loading) => dispatch(setUsersStep6LeadLoading(loading)),
    startDateParam,
    endDateParam
  );

  useOptifundTabData(
    userToken,
    websiteSharedUid,
    OptifundDashboardQueryIds.usersStep6QualifiedId,
    (data) => dispatch(setUsersStep6QualifiedData(data)),
    (loading) => dispatch(setUsersStep6QualifiedLoading(loading)),
    startDateParam,
    endDateParam
  );

  useOptifundTabData(
    userToken,
    websiteSharedUid,
    OptifundDashboardQueryIds.usersStep7AllId,
    (data) => dispatch(setUsersStep7AllData(data)),
    (loading) => dispatch(setUsersStep7AllLoading(loading)),
    startDateParam,
    endDateParam
  );

  useOptifundTabData(
    userToken,
    websiteSharedUid,
    OptifundDashboardQueryIds.usersStep7LeadId,
    (data) => dispatch(setUsersStep7LeadData(data)),
    (loading) => dispatch(setUsersStep7LeadLoading(loading)),
    startDateParam,
    endDateParam
  );

  useOptifundTabData(
    userToken,
    websiteSharedUid,
    OptifundDashboardQueryIds.usersStep7QualifiedId,
    (data) => dispatch(setUsersStep7QualifiedData(data)),
    (loading) => dispatch(setUsersStep7QualifiedLoading(loading)),
    startDateParam,
    endDateParam
  );

  const funnelSteps: FunnelStep[] = useMemo(
    () => (Array.isArray(usersData) ? mapMainFunnelApiRows(usersData) : EMPTY_STEPS),
    [usersData]
  );
  const leadToQualifiedSegments: DonutSegment[] = useMemo(
    () =>
      Array.isArray(usersLeadToQualifiedData)
        ? mapLeadToQualifiedSegments(usersLeadToQualifiedData)
        : EMPTY_SEGMENTS,
    [usersLeadToQualifiedData]
  );
  const qualifiedByZipSegments: DonutSegment[] = useMemo(
    () =>
      Array.isArray(usersQualifiedByZipData)
        ? mapQualifiedByZipSegments(usersQualifiedByZipData)
        : EMPTY_SEGMENTS,
    [usersQualifiedByZipData]
  );
  const leadToMatchedSegments: DonutSegment[] = useMemo(
    () => (Array.isArray(usersLeadToMatchedData) ? mapLeadToMatchedSegments(usersLeadToMatchedData) : EMPTY_SEGMENTS),
    [usersLeadToMatchedData]
  );
  const leadToPaidSegments: DonutSegment[] = useMemo(
    () => (Array.isArray(usersLeadToPaidData) ? mapLeadToPaidSegments(usersLeadToPaidData) : EMPTY_SEGMENTS),
    [usersLeadToPaidData]
  );

  const isStep1Loading = usersStep1AllLoading || usersStep1LeadLoading || usersStep1QualifiedLoading;
  const areStep1QueriesReady =
    !isStep1Loading &&
    Array.isArray(usersStep1AllData) &&
    Array.isArray(usersStep1LeadData) &&
    Array.isArray(usersStep1QualifiedData);
  const step1Section: StepBarSection | null = useMemo(
    () =>
      areStep1QueriesReady
        ? mapBorrowAmountStepSection(usersStep1AllData, usersStep1LeadData, usersStep1QualifiedData)
        : null,
    [areStep1QueriesReady, usersStep1AllData, usersStep1LeadData, usersStep1QualifiedData]
  );

  const isStep2Loading = usersStep2AllLoading || usersStep2LeadLoading || usersStep2QualifiedLoading;
  const areStep2QueriesReady =
    !isStep2Loading &&
    Array.isArray(usersStep2AllData) &&
    Array.isArray(usersStep2LeadData) &&
    Array.isArray(usersStep2QualifiedData);
  const step2Section: StepBarSection | null = useMemo(
    () =>
      areStep2QueriesReady
        ? mapLoanPurposeStepSection(usersStep2AllData, usersStep2LeadData, usersStep2QualifiedData)
        : null,
    [areStep2QueriesReady, usersStep2AllData, usersStep2LeadData, usersStep2QualifiedData]
  );

  const isStep3Loading = usersStep3AllLoading || usersStep3LeadLoading || usersStep3QualifiedLoading;
  const areStep3QueriesReady =
    !isStep3Loading &&
    Array.isArray(usersStep3AllData) &&
    Array.isArray(usersStep3LeadData) &&
    Array.isArray(usersStep3QualifiedData);
  const step3Section: StepBarSection | null = useMemo(
    () =>
      areStep3QueriesReady
        ? mapTimeInBusinessStepSection(usersStep3AllData, usersStep3LeadData, usersStep3QualifiedData)
        : null,
    [areStep3QueriesReady, usersStep3AllData, usersStep3LeadData, usersStep3QualifiedData]
  );

  const isStep4Loading = usersStep4AllLoading || usersStep4LeadLoading || usersStep4QualifiedLoading;
  const areStep4QueriesReady =
    !isStep4Loading &&
    Array.isArray(usersStep4AllData) &&
    Array.isArray(usersStep4LeadData) &&
    Array.isArray(usersStep4QualifiedData);
  const step4Section: StepBarSection | null = useMemo(
    () =>
      areStep4QueriesReady
        ? mapMonthlyRevenueStepSection(usersStep4AllData, usersStep4LeadData, usersStep4QualifiedData)
        : null,
    [areStep4QueriesReady, usersStep4AllData, usersStep4LeadData, usersStep4QualifiedData]
  );

  const isStep5Loading = usersStep5LeadLoading || usersStep5QualifiedLoading;
  const areStep5QueriesReady =
    !isStep5Loading && Array.isArray(usersStep5LeadData) && Array.isArray(usersStep5QualifiedData);
  const step5Section: StepDonutSection | null = useMemo(
    () => (areStep5QueriesReady ? mapBusinessBankingStepSection(usersStep5LeadData, usersStep5QualifiedData) : null),
    [areStep5QueriesReady, usersStep5LeadData, usersStep5QualifiedData]
  );

  const isStep6Loading = usersStep6AllLoading || usersStep6LeadLoading || usersStep6QualifiedLoading;
  const areStep6QueriesReady =
    !isStep6Loading &&
    Array.isArray(usersStep6AllData) &&
    Array.isArray(usersStep6LeadData) &&
    Array.isArray(usersStep6QualifiedData);
  const step6Section: StepBarSection | null = useMemo(
    () =>
      areStep6QueriesReady
        ? mapBusinessIndustryStepSection(usersStep6AllData, usersStep6LeadData, usersStep6QualifiedData)
        : null,
    [areStep6QueriesReady, usersStep6AllData, usersStep6LeadData, usersStep6QualifiedData]
  );

  const isStep7Loading = usersStep7AllLoading || usersStep7LeadLoading || usersStep7QualifiedLoading;
  const areStep7QueriesReady =
    !isStep7Loading &&
    Array.isArray(usersStep7AllData) &&
    Array.isArray(usersStep7LeadData) &&
    Array.isArray(usersStep7QualifiedData);
  const step7Section: StepBarSection | null = useMemo(
    () =>
      areStep7QueriesReady
        ? mapPersonalCreditStepSection(usersStep7AllData, usersStep7LeadData, usersStep7QualifiedData)
        : null,
    [areStep7QueriesReady, usersStep7AllData, usersStep7LeadData, usersStep7QualifiedData]
  );

  const total = funnelSteps.find((step) => step.label === "click_in")?.count ?? 0;
  const leads = funnelSteps.find((step) => step.label === "Contact details")?.count ?? 0;
  const qualified = funnelSteps.find((step) => step.label === "Qualified")?.count ?? 0;
  const leadToQualifiedTotal = leadToQualifiedSegments.reduce((sum, segment) => sum + segment.value, 0);
  const qualifiedByZipTotal = qualifiedByZipSegments.reduce((sum, segment) => sum + segment.value, 0);
  const leadToMatchedTotal = leadToMatchedSegments.reduce((sum, segment) => sum + segment.value, 0);
  const leadToPaidTotal = leadToPaidSegments.reduce((sum, segment) => sum + segment.value, 0);

  // This is the ONLY gate before rendering anything - the funnel/KPI/overview
  // phase (Story 8's "funnel and overview charts"). The 7 step sections
  // below load independently after this, each showing its own skeleton
  // until its own queries resolve (see UsersStepSections.tsx) - not gated
  // behind this flag.
  const isOverviewLoading =
    usersLoading ||
    usersLeadToQualifiedLoading ||
    usersQualifiedByZipLoading ||
    usersLeadToMatchedLoading ||
    usersLeadToPaidLoading ||
    !Array.isArray(usersData) ||
    !Array.isArray(usersLeadToQualifiedData) ||
    !Array.isArray(usersQualifiedByZipData) ||
    !Array.isArray(usersLeadToMatchedData) ||
    !Array.isArray(usersLeadToPaidData);

  return (
    <Stack spacing={2.5}>
      <UsersDateFilter dateRange={dateRange} onDateRangeChange={setDateRange} />

      {isOverviewLoading ? (
        <UsersSkel />
      ) : (
        <>
          <UsersKpiCards total={total} leads={leads} qualified={qualified} />
          <UsersFunnel steps={funnelSteps} total={total} />
          <UsersFunnelDonuts
            leadToQualifiedSegments={leadToQualifiedSegments}
            qualifiedByZipSegments={qualifiedByZipSegments}
            leadToMatchedSegments={leadToMatchedSegments}
            leadToPaidSegments={leadToPaidSegments}
            leads={leadToQualifiedTotal}
            qualified={qualifiedByZipTotal}
            matched={leadToMatchedTotal}
            paid={leadToPaidTotal}
          />
        </>
      )}

      <UsersStepSections
        step1Section={step1Section}
        step2Section={step2Section}
        step3Section={step3Section}
        step4Section={step4Section}
        step5Section={step5Section}
        step6Section={step6Section}
        step7Section={step7Section}
      />
    </Stack>
  );
}
